/* MMB / VO integration: let VO validate and save priced options, then open Dawn.
   VO's bcpo:afterAdd hook suppresses its default /cart redirect when it returns true.
   Keep VO's checkout handler: it is responsible for charging its price add-ons. */
(function () {
  'use strict';
  let boundJQuery, observedDrawer, observer, syncTimer, renderTimer, refreshPromise;
  const cartURL = () => window.routes?.cart_url || (window.Shopify?.routes?.root || '/') + 'cart';
  const drawer = () => document.querySelector('cart-drawer');
  async function json(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || 'Unable to update cart.');
    return data;
  }
  function showError(message) {
    const el = document.querySelector('#CartDrawer-CartErrors');
    if (el) el.textContent = message;
    else console.error('[MMB cart]', message);
  }
  let protectionPromise;
  const isProtection = (item, variant) => String(item.variant_id) === variant || item.properties?._mmb_protection === 'true';
  async function ensureProtection(cart) {
    const input = document.querySelector('[data-mmb-prot]');
    if (!input || !cart.items?.length) return cart;
    const variant = input.dataset.variant;
    const hasMerchandise = cart.items.some(item => !isProtection(item, variant));
    const existingLines = cart.items.filter(item => isProtection(item, variant));
    // Reconcile only when needed. A remembered opt-out always stays off.
    if (hasMerchandise && (cart.attributes?._mmb_protection_choice === 'disabled' || existingLines.length === 1 && existingLines[0].quantity === 1)) return cart;
    if (!hasMerchandise && !existingLines.length) return cart;
    if (protectionPromise) return protectionPromise;
    protectionPromise = (async () => {
      const current = await json(cartURL() + '.js');
      const existing = current.items.filter(item => isProtection(item, variant));
      const merchandise = current.items.some(item => !isProtection(item, variant));
      const options = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      input.disabled = true;
      // Removing the final graphic must also remove its protection fee.
      if (!merchandise) {
        if (!existing.length) return current;
        return json(cartURL() + '/update.js', { ...options, body: JSON.stringify({ updates: Object.fromEntries(existing.map(item => [item.key, 0])) }) });
      }
      if (current.attributes?._mmb_protection_choice === 'disabled') return current;
      if (!existing.length) {
        await json(cartURL() + '/add.js', { ...options, body: JSON.stringify({ items: [{ id: variant, quantity: 1, properties: { _mmb_protection: 'true' } }] }) });
      } else if (existing.length > 1 || existing[0].quantity !== 1) {
        await json(cartURL() + '/update.js', { ...options, body: JSON.stringify({ updates: Object.fromEntries(existing.map((item, i) => [item.key, i === 0 ? 1 : 0])) }) });
      }
      return json(cartURL() + '/update.js', { ...options, body: JSON.stringify({ attributes: { _mmb_protection_choice: 'enabled' } }) });
    })().finally(() => { input.disabled = false; protectionPromise = null; });
    return protectionPromise;
  }
  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }
  function syncDisplay() {
    const total = document.querySelector('#CartDrawer [data-mmb-total]');
    if (!total) return;
    // VO updates the total after Liquid renders; mirror its formatted value exactly.
    setText(document.querySelector('#CartDrawer .mmb-price'), total.textContent.trim());
    let cents = Number(total.dataset.cents);
    const app = window.bcpo;
    if (app?.cart2 && app.price_addons?.length && app.subtotal != null) {
      // Use VO's own adjusted line prices, never parse or re-add property strings.
      const hasAddons = app.price_addons.some((line, i) => line.price_with_addon !== app.cart2.items?.[i]?.price);
      if (hasAddons) cents = app.price_addons.reduce((sum, line, i) => {
        const item = app.cart2.items[i];
        return sum + item.quantity * (item.final_price + line.price_with_addon - item.price);
      }, 0);
    }
    const bar = document.querySelector('#CartDrawer [data-mmb-shipbar]');
    if (!bar || !Number.isFinite(cents)) return;
    const threshold = Number(bar.dataset.threshold);
    const remaining = Math.max(0, threshold - cents);
    const percent = threshold > 0 ? Math.min(100, Math.max(0, cents / threshold * 100)) : 100;
    const label = bar.querySelector('.mmb-shipbar__label');
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: bar.dataset.currency || 'USD' });
    setText(label, remaining ? `You're ${money.format(remaining / 100)} away from free shipping` : 'Free shipping unlocked!');
    label?.classList.toggle('mmb-shipbar__label--won', !remaining);
    const track = bar.querySelector('[role="progressbar"]');
    if (track?.getAttribute('aria-valuenow') !== String(Math.round(percent))) track?.setAttribute('aria-valuenow', String(Math.round(percent)));
    const fill = bar.querySelector('.mmb-shipbar__fill');
    if (fill && fill.style.width !== `${percent}%`) fill.style.width = `${percent}%`;
  }
  function scheduleSync() { clearTimeout(syncTimer); syncTimer = setTimeout(syncDisplay, 0); }
  async function applyAppPrices(cart) {
    const original = cart || await json(cartURL() + '.js');
    const current = await ensureProtection(original);
    if (current.item_count !== original.item_count) setTimeout(() => refresh(false), 0);
    const app = window.bcpo;
    if (app?.fixCartPrices && app.$) {
      app.cart2 = current;
      // Rebind VO's checkout listener to the newly rendered checkout button.
      app.addedCartEvent = false;
      app.fixCartPrices(false);
    }
    const variant = document.querySelector('[data-mmb-prot]')?.dataset.variant;
    const count = current.items.reduce((sum, item) => sum + (isProtection(item, variant) ? 0 : item.quantity), 0);
    document.querySelectorAll('[data-mmb-cart-count], [data-mmb-item-count]').forEach(el => setText(el, String(count)));
    syncDisplay();
  }
  async function refresh(open) {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      const d = drawer();
      if (!d) return;
      await customElements.whenDefined('cart-drawer');
      const url = new URL(cartURL(), location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const cart = await ensureProtection(await json(cartURL() + '.js'));
      const sections = await json(url.href);
      if (!sections['cart-drawer']) throw new Error('Cart added. Please open View Cart to review your items.');
      d.renderContents({ sections }, open);
      await applyAppPrices(cart);
    })().catch(error => { drawer()?.open?.(); showError(error.message); }).finally(() => { refreshPromise = null; });
    return refreshPromise;
  }
  function bindApp() {
    const $ = window.bcpo?.$;
    if (!$ || boundJQuery === $) return;
    boundJQuery = $;
    $(document).on('bcpo:afterAdd.mmbCart', function () {
      if (!drawer()) return;
      refresh(true);
      $('.r-apps-loader').addClass('r-apps-loader--hidden');
      window.bcpo.addToCart?.attr('disabled', false);
      return true; // VO has saved the options; prevent only its default redirect.
    });
    $(document).on('bcpo:cartPrices.mmbCart bcpo:done.mmbCart', scheduleSync);
  }
  function boot() {
    bindApp();
    const root = document.getElementById('CartDrawer');
    if (root && root !== observedDrawer) {
      observer?.disconnect();
      observedDrawer = root;
      observer = new MutationObserver(scheduleSync);
      // Observe text replacements from VO and Dawn. syncDisplay writes only changes.
      observer.observe(root, { subtree: true, childList: true, characterData: true });
    }
  }
  const previousReady = window.bcpoReady;
  window.bcpoReady = function () { previousReady?.apply(this, arguments); boot(); };
  document.addEventListener('DOMContentLoaded', () => { boot(); applyAppPrices().catch(() => {}); });
  document.addEventListener('load', event => { if (event.target.tagName === 'SCRIPT') boot(); }, true);
  // VO loads its private jQuery asynchronously after calling bcpoReady.
  let attempts = 0;
  const readyTimer = setInterval(() => { boot(); if (boundJQuery || ++attempts >= 120) clearInterval(readyTimer); }, 250);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('mmb:cart-rendered', event => {
    boot(); clearTimeout(renderTimer);
    renderTimer = setTimeout(() => applyAppPrices(event.detail?.cart).catch(error => showError(error.message)), 40);
  });
  // Bind at capture so header cart links are not sent to /cart by app handlers.
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-mmb-cart], #cart-icon-bubble');
    if (!link || !drawer() || typeof drawer().open !== 'function') return;
    event.preventDefault(); event.stopImmediatePropagation();
    drawer().open(link); refresh(false);
  }, true);
  document.addEventListener('change', async event => {
    const input = event.target.closest('[data-mmb-prot]');
    if (!input || input.disabled) return;
    const checked = input.checked;
    input.disabled = true;
    try {
      if (protectionPromise) await protectionPromise;
      const cart = await json(cartURL() + '.js');
      const existing = cart.items.filter(item => String(item.variant_id) === input.dataset.variant || item.properties?._mmb_protection === 'true');
      const options = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      if (checked && !existing.length) {
        await json(cartURL() + '/add.js', { ...options, body: JSON.stringify({ items: [{ id: input.dataset.variant, quantity: 1, properties: { _mmb_protection: 'true' } }] }) });
      } else if (!checked && existing.length) {
        const updates = Object.fromEntries(existing.map(item => [item.key, 0]));
        await json(cartURL() + '/update.js', { ...options, body: JSON.stringify({ updates }) });
      }
      await json(cartURL() + '/update.js', { ...options, body: JSON.stringify({ attributes: { _mmb_protection_choice: checked ? 'enabled' : 'disabled' } }) });
      await refresh(false);
    } catch (error) { input.checked = !checked; showError(error.message); }
    finally { input.disabled = false; }
  });
})();
